import {
  AtonWithRelations,
  AtonStatusOption,
  MonitorService,
  MonitorTypes,
} from '../types';
import { getIconForService, getVariantForState } from './utils';
import { useLocalization } from '@ais-ui/locale-management';

// Add interface for ServiceMatch
interface ServiceMatch {
  serviceDefinition: any;
  serviceData: any;
  serviceName: string;
}

export const findMonitorTypes = (
  aton: AtonWithRelations,
  services: any,
  messages: any
): MonitorTypes => {
  const serviceMonitors = findServiceMonitorTypes(aton);
  const stationMonitors = findStationMonitorTypes(aton, services, messages); // Pass services to station monitor creation

  return {
    services: [...serviceMonitors, ...stationMonitors],
  };
};

export const findServiceMonitorTypes = (
  aton: AtonWithRelations
): MonitorService[] => {
  const services: MonitorService[] = [];

  if (!aton.atonServices) {
    return services;
  }

  // Now atonServices is ServiceMatch[] instead of service definitions array
  aton.atonServices.forEach((serviceMatch: ServiceMatch) => {
    const service = serviceMatch.serviceDefinition; // Get the service definition
    const serviceKey = serviceMatch.serviceName; // Get the actual service key from services object

    // Check if service has threshold/range values
    const hasThresholdValues =
      (service.alarmHigh !== null && service.alarmHigh !== undefined) ||
      (service.alarmLow !== null && service.alarmLow !== undefined) ||
      (service.warningHigh !== null && service.warningHigh !== undefined) ||
      (service.warningLow !== null && service.warningLow !== undefined);

    if (hasThresholdValues) {
      // Create threshold or range monitor
      const monitor = createThresholdOrRangeMonitor(
        service,
        serviceMatch.serviceData,
        serviceKey
      );
      services.push(monitor);
    } else if (service.stateNames && service.stateNames.length > 0) {
      // Create status monitor for services with state names
      const monitor = createStatusMonitor(
        service,
        serviceMatch.serviceData,
        serviceKey
      );
      services.push(monitor);
    }
  });

  return services;
};

const createThresholdOrRangeMonitor = (
  service: any,
  serviceData: any,
  serviceKey: string
): MonitorService => {
  const hasHighAndLow = (
    high: number | null | undefined,
    low: number | null | undefined
  ) => high !== null && high !== undefined && low !== null && low !== undefined;

  // Determine if this is a range or threshold type
  const isAlarmRange = hasHighAndLow(service.alarmHigh, service.alarmLow);
  const isWarningRange = hasHighAndLow(service.warningHigh, service.warningLow);
  const isRangeType = isAlarmRange || isWarningRange;

  const monitor: MonitorService = {
    id: service.uuid,
    name: service.name,
    description: service.description,
    type: isRangeType ? 'range' : 'threshold',
    unit: getUnitForService(service),
    serviceKey: serviceKey, // Use the actual service key from services object
  };

  if (isRangeType) {
    // Range-based monitoring
    if (isAlarmRange) {
      monitor.alarmRange = {
        min: service.alarmLow!,
        max: service.alarmHigh!,
      };
    }
    if (isWarningRange) {
      monitor.warningRange = {
        min: service.warningLow!,
        max: service.warningLow!,
      };
    }
  } else {
    // Threshold-based monitoring
    if (service.alarmHigh !== null && service.alarmHigh !== undefined) {
      monitor.alarmThreshold = service.alarmHigh;
    } else if (service.alarmLow !== null && service.alarmLow !== undefined) {
      monitor.alarmThreshold = service.alarmLow;
    }

    if (service.warningHigh !== null && service.warningHigh !== undefined) {
      monitor.warningThreshold = service.warningHigh;
    } else if (
      service.warningLow !== null &&
      service.warningLow !== undefined
    ) {
      monitor.warningThreshold = service.warningLow;
    }
  }

  return monitor;
};

const createStatusMonitor = (
  service: any,
  serviceData: any,
  serviceKey: string
): MonitorService => {
  const statuses: AtonStatusOption[] = service.stateNames.map(
    (stateName: string, index: number) => ({
      id: `${service.uuid}-state-${index}`,
      label: stateName || `State ${index}`,
      icon: getIconForService(service, stateName),
      variant: getVariantForState(service, stateName, index),
      description: `${service.description || service.name} - ${stateName}`,
    })
  );

  return {
    id: service.uuid,
    name: service.name,
    description: service.description,
    type: 'status',
    statuses,
    serviceKey: serviceKey, // Use the actual service key from services object
  };
};

export const findStationMonitorTypes = (
  aton: AtonWithRelations,
  services: any,
  messages: any// Add services parameter to map to actual service keys
): MonitorService[] => {

  const servicesList: MonitorService[] = [];

  if (!aton.atonStation) {
    return servicesList;
  }

  const station = aton.atonStation;

  // Create services for each AIS field that exists
  if (station.aisTimeout) {
    const timeoutSeconds = parseTimeoutToSeconds(station.aisTimeout);

    const aisFields = [
      {
        key: 'aisIndicator',
        name: 'AIS Indicator',
        description: messages.atonFindStationMonitorTypesAisIndicatorDescription,
        serviceKey: findServiceKeyForAIS('indicator', services), // Find actual service key
      },
      {
        key: 'aisType21',
        name: 'AIS Type 21',
        description: messages.atonFindStationMonitorTypesAisType21Description,
        serviceKey: findServiceKeyForAIS('msg21', services), // Find actual service key
      },
      {
        key: 'aisType6',
        name: 'AIS Type 6',
        description: messages.atonFindStationMonitorTypesAisType6Description,
        serviceKey: findServiceKeyForAIS('msg6', services), // Find actual service key
      },
      {
        key: 'aisType8',
        name: 'AIS Type 8',
        description: messages.atonFindStationMonitorTypesAisType8Description,
        serviceKey: findServiceKeyForAIS('msg8', services), // Find actual service key
      },
    ];

    aisFields.forEach((field) => {
      if (field.key in station) {
        servicesList.push({
          id: `${station.uuid}-${field.key}`,
          name: field.name,
          description: field.description,
          unit: 'seconds',
          type: 'threshold',
          alarmThreshold: timeoutSeconds,
          serviceKey: field.serviceKey, // Store the actual service key for chart fetching
        });
      }
    });
  }

  // Position watchdog service
  if (
    station.positionWatchdog?.active &&
    station.positionWatchdog.maxDistance
  ) {
    servicesList.push({
      id: station.positionWatchdog.uuid as string,
      name: station.positionWatchdog.name || 'Position Watchdog',
      description: messages.atonFindStationPositionWatchdogDescription,
      unit: 'meters',
      type: 'threshold',
      alarmThreshold: station.positionWatchdog.maxDistance,
      serviceKey: findServiceKeyForAIS('watchdog', services), // Find watchdog service key
    });
  }

  return servicesList;
};

// Helper function to find the actual service key for AIS services
const findServiceKeyForAIS = (
  type: string,
  services: any
): string | undefined => {
  if (!services) {
    return undefined;
  }

  const serviceKeys = Object.keys(services);
  const typeLower = type.toLowerCase();

  const found = serviceKeys.find((key) => {
    const keyLower = key.toLowerCase();

    if (typeLower === 'msg21' && keyLower.includes('msg21')) return true;
    if (typeLower === 'msg6' && keyLower.includes('msg6')) return true;
    if (typeLower === 'msg8' && keyLower.includes('msg8')) return true;
    if (typeLower === 'indicator' && keyLower.includes('indicator'))
      return true;
    if (
      typeLower === 'watchdog' &&
      (keyLower.includes('watchdog') || keyLower.includes('position'))
    )
      return true;

    return false;
  });

  return found;
};

// Helper function to determine unit based on service name/type
const getUnitForService = (service: any): string | undefined => {
  const serviceName = service.name?.toLowerCase() || '';
  const serviceDescription = service.description?.toLowerCase() || '';

  // Check if it's an analog service (voltage)
  if (serviceName.includes('analog') || serviceDescription.includes('analog')) {
    return 'V';
  }

  // Add other unit mappings as needed
  return undefined;
};

// Helper function to parse ISO 8601 duration to seconds
export const parseTimeoutToSeconds = (timeout: string): number => {
  const timeMatch = timeout.match(/T(\d{2}):(\d{2}):(\d{2})/);
  if (timeMatch) {
    const hours = parseInt(timeMatch[1], 10);
    const minutes = parseInt(timeMatch[2], 10);
    const seconds = parseInt(timeMatch[3], 10);
    return hours * 3600 + minutes * 60 + seconds;
  }
  return 0;
};
